
import React, { useState, useEffect, useRef } from 'react';
import { useContent } from '../contexts/ContentContext';
import { Link } from 'react-router-dom';
import { X, Mail, Send, CheckCircle2, Terminal, ShieldAlert } from 'lucide-react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultSubject?: string;
}

const STATICFORMS_ACCESS_KEY = 'sf_blc2ghcmjnneb6722gdjhmng';
const RECAPTCHA_V2_SITE_KEY = '6LdcK1csAAAAAKQ5U6x1jbLtwqNze3Fdm6q1dlfa';

export const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, defaultSubject }) => {
  const { t, lang, consent, updateConsent } = useContent();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const recaptchaWidgetId = useRef<any>(null);



  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: 'full-cycle',
    message: ''
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Validation Logic
  const fieldErrors = {
    name: formData.name.trim().length < 2 ? (lang === 'en' ? 'Full name required' : 'Ime i prezime su obavezni') : null,
    email: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) ? (lang === 'en' ? 'Invalid email format' : 'Neispravan format emaila') : null,
    message: formData.message.trim().length < 10 ? (lang === 'en' ? 'Message too short' : 'Poruka je prekratka') : null,
    privacy: !agreeToPrivacy ? (lang === 'en' ? 'Consent required' : 'Saglasnost obavezna') : null,
    captcha: !captchaToken ? (lang === 'en' ? 'Complete reCAPTCHA' : 'Potvrdite reCAPTCHA') : null
  };

  const isFormValid = !fieldErrors.name && !fieldErrors.email && !fieldErrors.message && !fieldErrors.privacy && captchaToken !== '';

  const handlePrivacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setAgreeToPrivacy(isChecked);
    if (isChecked) {
      updateConsent({ marketing: true });
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      if (status !== 'success') {
        if (defaultSubject) setFormData(prev => ({ ...prev, projectType: defaultSubject }));
        setErrorMessage(null);

        // Render reCAPTCHA if consent is given (either via checkbox or global marketing consent)
        const shouldRender = agreeToPrivacy || consent.marketing;

        if (shouldRender) {
          const checkAndRender = () => {
            const container = document.getElementById('recaptcha-modal');
            if (window.grecaptcha && container && container.innerHTML === "") {
              try {
                recaptchaWidgetId.current = window.grecaptcha.render('recaptcha-modal', {
                  sitekey: RECAPTCHA_V2_SITE_KEY,
                  theme: 'dark',
                  callback: (token: string) => setCaptchaToken(token),
                  'expired-callback': () => setCaptchaToken(''),
                });
              } catch (e) {
                console.warn("reCAPTCHA already rendered", e);
              }
            } else if (!window.grecaptcha) {
              // If script is loading via ContentContext, wait for it
              setTimeout(checkAndRender, 500);
            }
          };

          // Initial check delay to allow DOM to settle
          setTimeout(checkAndRender, 500);
        }

        return () => {
          // Cleanup
          // We don't aggressively clear innerHTML here if we want to keep state, 
          // but typically we should clear if the modal closes.
          // Since this cleanup runs on dep change, we need to be careful.
          // Let's only clear if we are actually closing or unmounting.
          // However, checking 'isOpen' here in cleanup might refer to the closure value.
          // For safety in a modal, clearing on unmount/close is good.
          if (!isOpen) { // This check might be stale, but typically we clear when effect re-runs or unmounts.
            const container = document.getElementById('recaptcha-modal');
            if (container) container.innerHTML = "";
            recaptchaWidgetId.current = null;
          }
        };
      }
    } else {
      document.body.style.overflow = '';
      // Clear when closed
      const container = document.getElementById('recaptcha-modal');
      if (container) container.innerHTML = "";
      recaptchaWidgetId.current = null;
    }
  }, [isOpen, defaultSubject, status, agreeToPrivacy, consent.marketing]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true, privacy: true, captcha: true });

    if (!isFormValid) return;

    setStatus('submitting');
    setErrorMessage(null);

    try {
      const response = await fetch("https://api.staticforms.xyz/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          accessKey: STATICFORMS_ACCESS_KEY,
          name: formData.name,
          email: formData.email,
          subject: `Saltium Modal Inquiry`,
          message: `Project: ${formData.projectType}\nCompany: ${formData.company}\n\n${formData.message}`,
          replyTo: formData.email,
          "g-recaptcha-response": captchaToken
        }),
      });

      const result = await response.json();

      // Safeguard: StaticForms sometimes returns success: false but a message saying it was submitted
      const isActuallySuccess = result.success || (result.message && result.message.toLowerCase().includes('submitted successfully'));

      if (isActuallySuccess) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(result.message || "Archive transmission failed.");
        if (window.grecaptcha && recaptchaWidgetId.current !== null) {
          window.grecaptcha.reset(recaptchaWidgetId.current);
        }
        setCaptchaToken('');
      }
    } catch (error) {
      console.error("Modal Form error:", error);
      setStatus('error');
      setErrorMessage("Network sync failed. Please check connection.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 overflow-hidden">
      <div className="absolute inset-0 bg-[#0d0a0f]/95 backdrop-blur-xl" onClick={onClose}></div>

      <div className={`relative bg-[#1a141f] border border-white/10 rounded-[32px] md:rounded-[48px] w-full shadow-2xl overflow-y-auto bg-grid animate-in fade-in zoom-in duration-500 transition-all duration-700 ${status === 'success' ? 'max-w-md max-h-min' : 'max-w-4xl max-h-[90vh]'
        }`}>
        <button onClick={onClose} className="absolute top-6 right-6 text-gray-500 hover:text-white transition-colors z-50 p-2">
          <X size={24} />
        </button>

        <div className={status === 'success' ? 'p-10' : 'p-8 md:p-14'}>
          {status === 'success' ? (
            <div className="text-center py-4 animate-in fade-in zoom-in duration-700">
              <div className="relative inline-block mb-8">
                <div className="absolute inset-0 bg-emerald-500/20 blur-3xl animate-pulse rounded-full"></div>
                <div className="relative w-20 h-20 bg-emerald-500/10 border-2 border-emerald-500/30 rounded-[28px] flex items-center justify-center mx-auto shadow-xl">
                  <CheckCircle2 size={40} className="text-emerald-500" strokeWidth={1.5} />
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-gaming font-bold text-white mb-4 uppercase tracking-tighter leading-none">Transmission OK</h3>
              <p className="text-gray-400 text-sm font-light leading-relaxed max-w-[280px] mx-auto italic mb-10">
                {lang === 'en'
                  ? "Packet received. Our engineers will analyze your vision and reach out shortly."
                  : "Paket primljen. Naši inženjeri će analizirati vašu viziju i javiti se ubrzo."}
              </p>
              <div className="flex items-center justify-center gap-3 text-[9px] font-black uppercase tracking-[0.4em] text-gray-600 mb-8">
                <Terminal size={12} className="text-emerald-500/40" />
                <span>Secure Channel Closed</span>
              </div>
              <button onClick={onClose} className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl transition-all shadow-lg shadow-emerald-900/40">
                Exit Archive
              </button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-1 gap-12">
              <div>
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-accent/10 border border-accent/20 rounded-2xl flex items-center justify-center text-accent">
                    <Mail size={24} />
                  </div>
                  <h2 className="text-3xl md:text-5xl font-gaming font-bold text-white uppercase tracking-tighter leading-none">
                    {t('contact_title')}
                  </h2>
                </div>

                <form onSubmit={handleFormSubmit} className="space-y-6" noValidate>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-300 ml-1">{t('form_name')} *</label>
                      <input type="text" name="name" placeholder="Name" className={`w-full bg-black/40 border ${touched.name && fieldErrors.name && status === 'idle' ? 'border-red-500' : 'border-white/10'} rounded-2xl px-6 py-4 outline-none focus:border-accent transition-all text-white`} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} onBlur={() => setTouched({ ...touched, name: true })} />
                      {touched.name && fieldErrors.name && status === 'idle' && <p className="text-[10px] text-red-500 uppercase font-bold tracking-widest ml-1 animate-in slide-in-from-top-1">{fieldErrors.name}</p>}
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-300 ml-1">{t('form_email')} *</label>
                      <input type="email" name="email" placeholder="Email" className={`w-full bg-black/40 border ${touched.email && fieldErrors.email && status === 'idle' ? 'border-red-500' : 'border-white/10'} rounded-2xl px-6 py-4 outline-none focus:border-accent transition-all text-white`} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} onBlur={() => setTouched({ ...touched, email: true })} />
                      {touched.email && fieldErrors.email && status === 'idle' && <p className="text-[10px] text-red-500 uppercase font-bold tracking-widest ml-1 animate-in slide-in-from-top-1">{fieldErrors.email}</p>}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-300 ml-1">{t('form_company')}</label>
                      <input type="text" name="company" placeholder="Organization" className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-accent transition-all text-white" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-widest text-gray-300 ml-1">{t('form_project')}</label>
                      <select name="project_type" className="w-full bg-black/40 border border-white/10 rounded-2xl px-6 py-4 outline-none focus:border-accent transition-all text-white appearance-none cursor-pointer" value={formData.projectType} onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}>
                        <option value="full-cycle">Full-Cycle Game Dev</option>
                        <option value="optimization">Technical Optimization</option>
                        <option value="art">3D Art & Animation</option>
                        <option value="xr">XR & Spatial Design</option>
                        <option value="consulting">General Consulting</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-gray-300 ml-1">{t('form_message')} *</label>
                    <textarea name="message" placeholder="Message details..." className={`w-full bg-black/40 border ${touched.message && fieldErrors.message && status === 'idle' ? 'border-red-500' : 'border-white/10'} rounded-2xl px-6 py-4 outline-none focus:border-accent transition-all text-white h-32 resize-none`} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} onBlur={() => setTouched({ ...touched, message: true })} />
                    {touched.message && fieldErrors.message && status === 'idle' && <p className="text-[10px] text-red-500 uppercase font-bold tracking-widest ml-1 animate-in slide-in-from-top-1">{fieldErrors.message}</p>}
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-4 pt-2">
                      <input type="checkbox" id="modal-privacy" checked={agreeToPrivacy} onChange={handlePrivacyChange} className="w-4 h-4 accent-accent cursor-pointer" />
                      <label htmlFor="modal-privacy" className={`text-[10px] font-bold uppercase tracking-widest ${touched.privacy && fieldErrors.privacy && status === 'idle' ? 'text-red-500 font-black' : 'text-gray-300'} cursor-pointer`}>
                        {t('form_consent')} <Link to="/privacy-policy" target="_blank" className="text-accent underline hover:text-white transition-colors">{t('privacy_title')}</Link>
                      </label>
                    </div>
                    {touched.privacy && fieldErrors.privacy && status === 'idle' && <p className="text-[10px] text-red-500 uppercase font-bold tracking-widest ml-8 animate-in slide-in-from-top-1">{fieldErrors.privacy}</p>}
                  </div>

                  <div className="flex flex-col gap-6 pt-6">
                    <div id="recaptcha-modal" data-theme="dark"></div>
                    {touched.captcha && fieldErrors.captcha && status === 'idle' && <p className="text-[10px] text-red-500 uppercase font-bold tracking-widest ml-1">{fieldErrors.captcha}</p>}

                    {errorMessage && status === 'error' && (
                      <div className="text-red-500 text-[10px] font-bold uppercase tracking-[0.2em] bg-red-500/10 p-4 rounded-xl border border-red-500/20 animate-in fade-in">
                        <ShieldAlert size={14} className="inline mr-2" /> {errorMessage}
                      </div>
                    )}

                    <button
                      id="cta-contact-modal-submit"
                      type="submit"
                      disabled={status === 'submitting'}
                      className={`btn-primary w-full py-5 group transition-all ${!isFormValid && status === 'idle' ? 'opacity-50 grayscale cursor-not-allowed' : ''}`}
                    >
                      {status === 'submitting' ? 'Transmitting...' : (
                        <span className="flex items-center justify-center gap-3">
                          {t('form_btn')} <Send size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
